import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { getHashdata } from "../components/Tabs/Utils/hashFunctions";

const readHashValue = (key) => {
  const raw = getHashdata(window.location.hash, key);
  if (raw == null) return "";
  try {
    return decodeURIComponent(raw);
  } catch {
    return raw;
  }
};

/**
 * One key of the URL hash, kept current as it changes.
 *
 * Both ways it can change are watched, because neither covers the other:
 *
 * - the tab components write their selection straight onto
 *   window.location.hash rather than through the router (see NavTabs'
 *   handleChange and hashFunctions' setHash). The router never sees that,
 *   but the browser fires "hashchange" for it.
 * - a navigate() carrying a hash - the projects tree opening a document on
 *   this page (buildEstimationHref) - goes through history.pushState, which
 *   changes the hash WITHOUT firing "hashchange". Only the router's location
 *   reports it, and only on its own terms: `key` changes on every navigation
 *   so a repeat of the same link still counts.
 *
 * For reading which tab is open from outside the tabs themselves.
 */
export default function useHashValue(key) {
  const location = useLocation();
  const [value, setValue] = useState(() => readHashValue(key));

  useEffect(() => {
    const sync = () => setValue(readHashValue(key));
    sync();
    window.addEventListener("hashchange", sync);
    return () => window.removeEventListener("hashchange", sync);
  }, [key, location.key, location.hash]);

  return value;
}
